import {
  Box,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import EmptyPlaceholder from '../../../../components/Table/EmptyPlaceholder';
import LangEnum from '../../../../lang/LangEnum';
import { useSignedContractsStore } from '../../../../stores/SignedContracts/SignedContracts.store';
import useTeceoStyles from '../../../../useTeceoStyles';
import { formatDate, userTimeZone } from '../../../../utils/dates';
import { EMPTY_STOCK_PATH } from '../../../../utils/images.constants';
import { useTranslation } from '../../../translation/TranslationContext';
import { useSignatureContext } from '../contexts/signature.context';
import { SignedContract } from '../signed-contract';
import ContractCellActions from './ContractCellActions';
import ContractHistoryActions from './ContractHistoryActions';

const useStyles = makeStyles()({
  minSizeCell: {
    minWidth: 200,
  },
  cellSizeSignature: {
    minWidth: 160,
    maxWidth: 280,
  },
  cellSizeVersion: {
    width: 96,
  },
  cellSizeCreatedAt: {
    width: 200,
  },
  paddingCellLeft: {
    paddingLeft: '18px',
  },
});

type ContractListProps = {
  signedContracts: SignedContract[];
};

const formatDateTime = (date: Date) => formatDate(date, 'P p', userTimeZone);

const ContractList = ({ signedContracts }: ContractListProps): JSX.Element => {
  const { classes } = useStyles();

  return (
    <>
      {signedContracts.map((signed) => (
        <TableRow key={signed.id}>
          <TableCell className={classes.paddingCellLeft} />
          <TableCell className={classes.cellSizeVersion}>
            <Typography>{signed.version}</Typography>
          </TableCell>
          <TableCell className={classes.cellSizeCreatedAt}>
            <Typography>{formatDateTime(signed.createdAt)}</Typography>
          </TableCell>
          <TableCell className={classes.cellSizeSignature}>
            <ContractCellActions contract={signed} signature={signed.userBrand} signingAs="BRAND" />
          </TableCell>
          <TableCell className={classes.cellSizeSignature}>
            <ContractCellActions contract={signed} signature={signed.userStore} signingAs="STORE" />
          </TableCell>
          <TableCell className={classes.minSizeCell} align="right">
            <ContractHistoryActions signed={signed} />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
};

const ContractHistoryTable = (): JSX.Element => {
  const { isLoading } = useSignatureContext();
  const { signedContracts } = useSignedContractsStore();
  const { translate } = useTranslation();
  const { classes: teceoClasses } = useTeceoStyles();
  const { classes } = useStyles();

  if (isLoading) {
    return (
      <Box
        sx={{
          width: '100%',
        }}
      >
        <LinearProgress />
      </Box>
    );
  }

  if (signedContracts.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <EmptyPlaceholder
          width={250}
          height={250}
          src={EMPTY_STOCK_PATH}
          message={translate(LangEnum.ORDER_CONTRACT_HISTORY_EMPTY_LIST)}
        />
      </Box>
    );
  }

  return (
    <TableContainer
      style={{ width: '100%', height: '100%' }}
      className={teceoClasses.tableContainerOptimezedToModal}
    >
      <Table aria-label="simple table" padding="none">
        <TableHead>
          <TableRow>
            <TableCell className={classes.paddingCellLeft} />
            <TableCell className={classes.cellSizeVersion}>
              <Typography variant="subtitle1" noWrap>
                {translate(LangEnum.ORDER_CONTRACT_HISTORY_TABLE_CELL_VERSION)}
              </Typography>
            </TableCell>
            <TableCell className={classes.cellSizeCreatedAt}>
              <Typography variant="subtitle1" noWrap>
                {translate(LangEnum.ORDER_CONTRACT_HISTORY_TABLE_CELL_CREATED_AT)}
              </Typography>
            </TableCell>
            <TableCell className={classes.cellSizeSignature}>
              <Typography variant="subtitle1" noWrap>
                {translate(LangEnum.ORDER_CONTRACT_HISTORY_TABLE_CELL_SIGNED_BRAND)}
              </Typography>
            </TableCell>
            <TableCell className={classes.cellSizeSignature}>
              <Typography variant="subtitle1" noWrap>
                {translate(LangEnum.ORDER_CONTRACT_HISTORY_TABLE_CELL_SIGNED_STORE)}
              </Typography>
            </TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          <ContractList signedContracts={signedContracts} />
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ContractHistoryTable;
